import React, { useState, useEffect } from 'react';
import { AlertTriangle, X, ChevronRight } from 'lucide-react';
import { apiClient } from '../../services/api/client';

interface AlertBannerProps {
  criticalCount?: number;
  onNavigate?: (path: string) => void;
}

export default function AlertBanner({ criticalCount, onNavigate }: AlertBannerProps) {
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);
  const [fetchedCount, setFetchedCount] = useState(0);

  // Self-fetch the real open-critical-issue count when the caller doesn't
  // supply one (App passes a hardcoded 0). Polls every 60s.
  useEffect(() => {
    if (criticalCount && criticalCount > 0) return;
    let alive = true;
    const load = async () => {
      try {
        // `limit`, not `page_size`: GET /api/v1/issues paginates on `limit`
        // (default 50) and silently ignores `page_size`, so this count-only
        // probe was pulling 50 fully-hydrated issues — 914 KB — on every route
        // mount and again every 60s. With `limit=1` it is 53 KB.
        // apiClient, not raw fetch: this hand-injected X-Tenant-ID and nothing
        // else — no Authorization header, no correlation id, no error-envelope
        // parsing, no isFallback signal. On this deployment /api/v1/issues
        // currently answers 200 unauthenticated so the banner does work; the
        // point is that it only works by accident. Wherever the route IS
        // auth-gated the poll 401s and `if (!res.ok) return` swallows it, and
        // the banner stays hidden however many criticals are open.
        const { data: j, error } = await apiClient.get<any>(
          '/api/v1/issues', { severity: 'critical', status: 'open', limit: 1 },
        );
        if (error) return;
        // Only `total` answers "how many are open". Falling back to items.length
        // would report "1 critical issue" whenever the field went missing.
        if (alive) setFetchedCount(Number(j?.total ?? 0) || 0);
      } catch { /* offline — banner stays hidden */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [criticalCount]);

  const effectiveCount = (criticalCount && criticalCount > 0) ? criticalCount : fetchedCount;

  useEffect(() => {
    setVisible(effectiveCount > 0 && !dismissed);
  }, [effectiveCount, dismissed]);

  if (!visible) return null;

  return (
    <div
      className="alert-banner-critical flex items-center gap-3 px-4 py-2.5 text-sm"
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-center gap-2 flex-1">
        <div className="p-1 rounded bg-red-500/15">
          <AlertTriangle className="w-4 h-4 text-red-400" />
        </div>
        <span className="text-red-300 font-medium">
          {effectiveCount} critical {effectiveCount === 1 ? 'issue' : 'issues'} require immediate attention
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={() => onNavigate?.('/issues?severity=critical')}
          className="text-xs text-red-300 hover:text-red-200 font-medium flex items-center gap-1 px-2.5 py-1 rounded-md bg-red-500/10 hover:bg-red-500/20 transition-colors"
        >
          Review now
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 text-red-400/50 hover:text-red-300 rounded transition-colors"
          aria-label="Dismiss alert"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
